import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BackButton, DataTable, SearchFilterBar, type ColumnDef, type FilterDef, type SortState } from '@shared-ui/components';
import { TopicSelector } from '@shared-ui/components/TopicSelector';
import { adminApi } from '../api/adminApi';
import { fetchWithAdmin } from '../api/fetchHelper';
import { LearningMomentTabs } from './learning-moments/LearningMomentTabs';
import { ExemplarQuestionPicker } from './learning-moments/ExemplarQuestionPicker';
import { ConceptPicker } from './learning-moments/ConceptPicker';
import './LearningMoments.css';

// ── Types ────────────────────────────────────────────────────────────────

type Scope = 'module' | 'subject' | 'generic';
/** What the student's card offers. A drill has a recognisable class of question
 *  behind it; a habit is a discipline no practice set instils, and offering one
 *  a drill promises what the set cannot deliver. Unset ⇒ no practice CTA. */
type PracticeKind = 'drill' | 'habit';
/** What must change for the student — independent of how it is acquired. */
type Needs = 'knowledge' | 'technique';

interface LearningMoment {
  id: string;
  scope: Scope;
  moduleIds?: string[];
  subjectIds?: string[];
  name: string;
  description: string;
  /** Solo moments only — a family member's rule lives on the group. */
  detectionHint?: string;
  exampleScenario?: string;
  tags?: string[];
  difficulty?: string[];
  exemplarQuestionIds?: string[];
  practiceKind?: PracticeKind;
  needs?: Needs;
  conceptIds?: string[];
  /** The family that owns detection for this moment. */
  groupId?: string;
  isActive: boolean;
  /** Encounters recorded against this definition — how often it actually fired.
   *  Supplied by the list endpoint; absent on a single-moment fetch. */
  hitCount?: number;
  missCount?: number;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

interface FormData {
  name: string;
  description: string;
  detectionHint: string;
  exampleScenario: string;
  scope: Scope;
  /** Module ids, chosen through the topic picker — an array, unlike the other
   *  list-ish fields, because nobody types a module id by hand. */
  moduleIds: string[];
  subjectIds: string;
  tags: string;
  difficulty: string;
  practiceKind: PracticeKind | '';
  needs: Needs | '';
  /** Where "learn it" points — ids from the concept registry, knowledge only. */
  conceptIds: string[];
  /** Author-pinned practice questions — chosen through the search box, so an
   *  array of ids rather than something anyone types. */
  exemplarQuestionIds: string[];
}

/** A family of moments sharing one detection rule. Authored in git and synced
 *  at deploy — read-only here, because a second place to edit the rule is how
 *  the copies that made groups necessary got made in the first place. */
interface LearningMomentGroup {
  id: string;
  name: string;
  detectionInstruction: string;
  memberIds: string[];
  fallbackMemberId?: string;
  isActive: boolean;
}

// ── API ──────────────────────────────────────────────────────────────────

const api = {
  listGroups: () => fetchWithAdmin<LearningMomentGroup[]>('/console/learning-moment-groups'),
  list: (params?: { scope?: string; activeOnly?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.scope) q.set('scope', params.scope);
    if (params?.activeOnly !== undefined) q.set('activeOnly', String(params.activeOnly));
    const qs = q.toString();
    return fetchWithAdmin<LearningMoment[]>(`/console/learning-moments${qs ? `?${qs}` : ''}`);
  },
  create: (data: Partial<LearningMoment>) =>
    fetchWithAdmin<LearningMoment>('/console/learning-moments', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<LearningMoment>) =>
    fetchWithAdmin<LearningMoment>(`/console/learning-moments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  remove: (id: string) =>
    fetchWithAdmin<{ id: string; deactivated: boolean }>(`/console/learning-moments/${id}`, {
      method: 'DELETE',
    }),
};

// ── Helpers ──────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const SCOPE_LABELS: Record<Scope, string> = { module: 'Module', subject: 'Subject', generic: 'Generic' };
const SUBJECTS = ['maths', 'physics', 'chemistry', 'biology'];

function emptyForm(): FormData {
  return { name: '', description: '', detectionHint: '', exampleScenario: '', scope: 'subject', moduleIds: [], subjectIds: '', tags: '', difficulty: '', practiceKind: '', needs: '', conceptIds: [], exemplarQuestionIds: [] };
}

function toForm(m: LearningMoment): FormData {
  return {
    name: m.name,
    description: m.description,
    detectionHint: m.detectionHint ?? '',
    exampleScenario: m.exampleScenario || '',
    scope: m.scope,
    moduleIds: m.moduleIds || [],
    subjectIds: (m.subjectIds || []).join(', '),
    tags: (m.tags || []).join(', '),
    difficulty: (m.difficulty || []).join(', '),
    practiceKind: m.practiceKind ?? '',
    needs: m.needs ?? '',
    conceptIds: m.conceptIds || [],
    exemplarQuestionIds: m.exemplarQuestionIds || [],
  };
}

function fromForm(form: FormData): Partial<LearningMoment> {
  const split = (s: string) => s.split(',').map(x => x.trim()).filter(Boolean);
  return {
    name: form.name,
    description: form.description,
    // A family member's box is empty because the family owns the rule; sending
    // '' would read as "cleared it".
    detectionHint: form.detectionHint || undefined,
    exampleScenario: form.exampleScenario || undefined,
    scope: form.scope,
    moduleIds: form.scope === 'module' ? form.moduleIds : undefined,
    subjectIds: form.scope === 'subject' ? split(form.subjectIds) : undefined,
    tags: split(form.tags).length > 0 ? split(form.tags) : undefined,
    difficulty: split(form.difficulty).length > 0 ? split(form.difficulty) : undefined,
    practiceKind: form.practiceKind || undefined,
    needs: form.needs || undefined,
    // Concepts belong to a knowledge remedy; on a technique they are
    // classification nothing reads.
    conceptIds: form.needs === 'knowledge' && form.conceptIds.length > 0 ? form.conceptIds : undefined,
    exemplarQuestionIds: form.exemplarQuestionIds.length > 0 ? form.exemplarQuestionIds : undefined,
  };
}

// ── Component ────────────────────────────────────────────────────────────

export function LearningMoments() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // One bag, because SearchFilterBar owns the controls: `scope` and `status`
  // are server-side (they change the query), `family` and `targeting` narrow
  // client-side — both are already on every row the list endpoint returns, and
  // the catalogue is small enough that a round trip per selection would be the
  // slower answer.
  const [filters, setFilters] = useState<Record<string, string>>({
    scope: '', status: 'active', family: '', targeting: '',
  });
  const [sort, setSort] = useState<SortState>({ sortBy: 'updated', sortOrder: 'desc' });
  const [editing, setEditing] = useState<LearningMoment | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormData>(emptyForm());

  const { data: groups } = useQuery({ queryKey: ['learning-moment-groups'], queryFn: api.listGroups });
  const groupById = useMemo(() => new Map((groups ?? []).map(g => [g.id, g])), [groups]);
  const editingGroup = editing?.groupId ? groupById.get(editing.groupId) : undefined;

  const { data: moments, isLoading } = useQuery({
    queryKey: ['learning-moments', filters.scope, filters.status],
    queryFn: () => api.list({
      scope: filters.scope || undefined,
      activeOnly: filters.status === 'active',
    }),
  });


  // Module catalog for the scope picker and for naming the ids a moment
  // already targets. Slim + active-only (no ?full), all subjects in one read —
  // a moment's modules are not constrained to one subject.
  const { data: modulesData } = useQuery({
    queryKey: ['modules', 'all'],
    queryFn: () => adminApi.getModules(),
  });
  const modules = useMemo(() => modulesData?.modules ?? [], [modulesData]);

  const selectorModules = useMemo(() => modules.map(m => ({
    id: m.id, displayName: m.displayName, subject: m.subject,
    isTopModule: m.isTopModule ?? (m.moduleLevel === 'module'),
    moduleLevel: m.moduleLevel ?? 'chapter', parentModuleId: m.parentModuleId,
    sortOrder: m.sortOrder ?? 0, grades: m.grades,
  })), [modules]);

  /** "Physics · Kinematics › Projectile Motion" — the ancestors are what tell
   *  two identically-named topics apart, and the id alone tells you nothing. */
  const moduleLabel = useMemo(() => {
    const byId = new Map(modules.map(m => [m.id, m]));
    return (id: string) => {
      const mod = byId.get(id);
      if (!mod) return id;
      const parent = mod.parentModuleId ? byId.get(mod.parentModuleId) : undefined;
      const grandparent = parent?.parentModuleId ? byId.get(parent.parentModuleId) : undefined;
      const path = [grandparent?.displayName, parent?.displayName, mod.displayName].filter(Boolean).join(' › ');
      return `${mod.subject} · ${path}`;
    };
  }, [modules]);

  /** Every distinct targeting value in the catalogue: subject names, module
   *  labels, and 'All' for the generic ones. Derived rather than hardcoded — a
   *  filter offering a subject no moment targets is a dead option. */
  const targetOptions = useMemo(() => {
    const out = new Set<string>();
    for (const m of moments ?? []) {
      if (m.scope === 'generic') out.add('All');
      for (const x of m.subjectIds ?? []) out.add(x.charAt(0).toUpperCase() + x.slice(1));
      for (const id of m.moduleIds ?? []) out.add(moduleLabel(id));
    }
    return [...out].sort();
  }, [moments, moduleLabel]);

  const targetsOf = (m: LearningMoment): string[] =>
    m.scope === 'generic' ? ['All']
      : m.scope === 'subject' ? (m.subjectIds ?? []).map(x => x.charAt(0).toUpperCase() + x.slice(1))
        : (m.moduleIds ?? []).map(moduleLabel);

  const rows = useMemo(() => (moments ?? []).filter(m => {
    if (filters.family === 'solo' && m.groupId) return false;
    if (filters.family && filters.family !== 'solo' && m.groupId !== filters.family) return false;
    if (filters.targeting && !targetsOf(m).includes(filters.targeting)) return false;
    return true;
  }), [moments, filters.family, filters.targeting, moduleLabel]);

  const filterDefs: FilterDef[] = [
    {
      key: 'scope', kind: 'select', placeholder: 'All Scopes',
      options: (['generic', 'subject', 'module'] as Scope[]).map(v => ({ value: v, label: SCOPE_LABELS[v] })),
    },
    {
      key: 'targeting', kind: 'select', placeholder: 'All Targeting',
      options: targetOptions.map(t => ({ value: t, label: t })),
    },
    {
      key: 'family', kind: 'select', placeholder: 'All Families',
      options: [
        ...(groups ?? []).map(g => ({ value: g.id, label: g.name })),
        { value: 'solo', label: 'Solo — no family' },
      ],
    },
    {
      key: 'status', kind: 'select', placeholder: 'All (incl. inactive)',
      options: [{ value: 'active', label: 'Active only' }],
    },
  ];

  /** `sortValue` on every column, so DataTable does the ordering: the sort key
   *  is rarely the rendered text — a status sorts by rank, a name by its
   *  lowercased form, a date by its raw ISO string. */
  const columns: ColumnDef<LearningMoment>[] = [
    {
      key: 'name', header: 'Name', sortable: true, mobile: 'title', hideable: false,
      sortValue: m => m.name.toLowerCase(),
      render: m => (
        <>
          <div className="lm-name">{m.name}</div>
          <div className="lm-desc-preview">{m.description.slice(0, 80)}{m.description.length > 80 ? '...' : ''}</div>
        </>
      ),
    },
    {
      key: 'scope', header: 'Scope', sortable: true, className: 'lm-scope',
      sortValue: m => SCOPE_LABELS[m.scope],
      render: m => SCOPE_LABELS[m.scope],
    },
    {
      key: 'targeting', header: 'Targeting', sortable: true, className: 'lm-targeting',
      sortValue: m => targetsOf(m).join(', ').toLowerCase(),
      render: m => targetsOf(m).join(', '),
    },
    {
      key: 'family', header: 'Family', sortable: true, className: 'lm-family',
      // Solos sort last, under a key no family name can reach, so sorting by
      // family reads as families first rather than scattering the ungrouped.
      sortValue: m => (m.groupId ? groupById.get(m.groupId)?.name ?? m.groupId : '\uffff').toLowerCase(),
      render: m => (m.groupId
        ? <>
            {groupById.get(m.groupId)?.name ?? 'in a family'}
            {groupById.get(m.groupId)?.fallbackMemberId === m.id && <div className="lm-hint">fallback</div>}
          </>
        : <span className="lm-hint">solo</span>),
    },
    {
      key: 'practice', header: 'Practice', sortable: true, className: 'lm-practice-kind',
      sortValue: m => `${m.needs ?? 'zz'} ${m.practiceKind ?? 'zz'}`,
      render: m => (
        <>
          {m.practiceKind ?? <span className="lm-hint">not set</span>}
          {m.needs && <span className="lm-hint"> · {m.needs}</span>}
        </>
      ),
    },
    // A dash, not a 0: nothing has been recorded either way, which is a
    // different thing from "tried and never landed".
    {
      key: 'hits', header: 'Hits', sortable: true, className: 'lm-num',
      sortValue: m => m.hitCount ?? 0,
      render: m => (m.hitCount ? m.hitCount.toLocaleString() : '—'),
    },
    {
      key: 'misses', header: 'Misses', sortable: true, className: 'lm-num',
      sortValue: m => m.missCount ?? 0,
      render: m => (m.missCount ? m.missCount.toLocaleString() : '—'),
    },
    // Plain text, not a badge — an ordinary attribute of the row, not something
    // that needs to compete for the eye.
    {
      key: 'status', header: 'Status', sortable: true,
      sortValue: m => (m.isActive ? 1 : 0),
      render: m => (
        <span className={m.isActive ? 'lm-status-active' : 'lm-status-inactive'}>
          {m.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'updated', header: 'Updated', sortable: true, className: 'lm-date',
      sortValue: m => m.updatedAt,
      render: m => formatDate(m.updatedAt),
    },
    {
      key: 'actions', header: '', className: 'lm-col-actions', hideable: false, mobile: 'accessory',
      // stopPropagation so a click here doesn't also fire the row's navigate.
      render: m => (
        <div className="lm-actions">
          <button
            className="lm-icon-btn" title="Edit" aria-label={`Edit ${m.name}`}
            onClick={(e) => { e.stopPropagation(); handleEdit(m); }}
          >✎</button>
          <button
            className="lm-icon-btn" title="Hits / Misses" aria-label={`Hits and misses for ${m.name}`}
            onClick={(e) => { e.stopPropagation(); navigate(`/learning-moments/encounters?type=${m.id}`); }}
          >↗</button>
        </div>
      ),
    },
  ];

  const saveMutation = useMutation({
    mutationFn: () => {
      const data = fromForm(form);
      return editing ? api.update(editing.id, data) : api.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['learning-moments'] });
      setEditing(null);
      setCreating(false);
      setForm(emptyForm());
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['learning-moments'] });
    },
  });

  const handleEdit = (m: LearningMoment) => {
    setEditing(m);
    setCreating(false);
    setForm(toForm(m));
  };

  const handleCreate = () => {
    setEditing(null);
    setCreating(true);
    setForm(emptyForm());
  };

  const handleCancel = () => {
    setEditing(null);
    setCreating(false);
    setForm(emptyForm());
  };

  const updateField = (field: Exclude<keyof FormData, 'moduleIds' | 'exemplarQuestionIds'>, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addModule = (id: string) => {
    if (!id) return;
    setForm(prev => prev.moduleIds.includes(id) ? prev : { ...prev, moduleIds: [...prev.moduleIds, id] });
  };

  const removeModule = (id: string) => {
    setForm(prev => ({ ...prev, moduleIds: prev.moduleIds.filter(x => x !== id) }));
  };

  // ── Form view ──────────────────────────────────────────────────────
  if (creating || editing) {
    return (
      <div className="lm-page">
        <BackButton label="list" onClick={handleCancel} />
        <h1>{editing ? 'Edit Learning Moment' : 'Create Learning Moment'}</h1>

        <div className="lm-form">
          <div className="lm-form-field">
            <label>Name *</label>
            <input type="text" value={form.name} onChange={e => updateField('name', e.target.value)} placeholder="e.g. Premature Multiplication" />
          </div>

          <div className="lm-form-field">
            <label>Description * <span className="lm-hint">Student-facing explanation</span></label>
            <textarea value={form.description} onChange={e => updateField('description', e.target.value)} placeholder="Explain what this learning moment means for the student" rows={5} />
          </div>

          {editing?.groupId ? (
            <div className="lm-form-field">
              <label>
                Detection — owned by the family “{editingGroup?.name ?? editing.groupId}”
                <span className="lm-hint">
                  This moment has no rule of its own. Its family's instruction decides which
                  member is reported and in what order, which is why it is authored in one
                  place (git) rather than edited per member here.
                </span>
              </label>
              <pre className="lm-group-rule">{editingGroup?.detectionInstruction ?? 'The family is not in this environment yet — deploy to sync it.'}</pre>
              {editingGroup && (
                <p className="lm-hint">
                  Order (most specific first):{' '}
                  {editingGroup.memberIds
                    .map(id => `${moments?.find(m => m.id === id)?.name ?? id}${id === editingGroup.fallbackMemberId ? ' (fallback)' : ''}`)
                    .join(' → ')}
                </p>
              )}
            </div>
          ) : (
            <div className="lm-form-field">
              <label>Detection Hint * <span className="lm-hint">Instructions for the LLM</span></label>
              <textarea value={form.detectionHint} onChange={e => updateField('detectionHint', e.target.value)} placeholder="How should the LLM detect this in a student's solution?" rows={8} />
            </div>
          )}

          <div className="lm-form-field">
            <label>Example Scenario <span className="lm-hint">Optional — concrete example for LLM context</span></label>
            <textarea value={form.exampleScenario} onChange={e => updateField('exampleScenario', e.target.value)} placeholder="e.g. Given (4/3)*(9/12), computing 36/36 instead of cancelling first" rows={6} />
          </div>

          <div className="lm-form-row">
            <div className="lm-form-field">
              <label>Scope *</label>
              <select value={form.scope} onChange={e => updateField('scope', e.target.value)}>
                <option value="generic">Generic (all questions)</option>
                <option value="subject">Subject-scoped</option>
                <option value="module">Module-scoped</option>
              </select>
            </div>

            {form.scope === 'subject' && (
              <div className="lm-form-field">
                <label>Subjects</label>
                <div className="lm-checkbox-group">
                  {SUBJECTS.map(s => {
                    const selected = form.subjectIds.split(',').map(x => x.trim()).filter(Boolean);
                    const isChecked = selected.includes(s);
                    return (
                      <label key={s} className="check-label">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            const next = isChecked ? selected.filter(x => x !== s) : [...selected, s];
                            updateField('subjectIds', next.join(', '));
                          }}
                        />
                        <span>{s.charAt(0).toUpperCase() + s.slice(1)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {form.scope === 'module' && (
              <div className="lm-form-field">
                <label>
                  Modules
                  <span className="lm-hint">
                    Pick as many as apply. A question matches on the module it is filed under
                    itself — choosing a chapter does not pull in its topics.
                  </span>
                </label>
                <div className="lm-module-chips">
                  {form.moduleIds.length === 0
                    ? <span className="lm-module-empty">None yet — this moment will never be a candidate.</span>
                    : form.moduleIds.map(id => (
                      <span key={id} className="lm-module-chip">
                        {moduleLabel(id)}
                        <button type="button" onClick={() => removeModule(id)} aria-label={`Remove ${moduleLabel(id)}`}>✕</button>
                      </span>
                    ))}
                </div>
                <TopicSelector
                  modules={selectorModules}
                  value=""
                  placeholder="Search module / chapter / topic…"
                  onChange={addModule}
                />
              </div>
            )}
          </div>

          <div className="lm-form-row">
            <div className="lm-form-field">
              <label>
                Practice
                <span className="lm-hint">
                  A drill has a class of question behind it; a habit is a discipline no practice
                  set instils. Unset means the card offers no practice.
                </span>
              </label>
              <select value={form.practiceKind} onChange={e => updateField('practiceKind', e.target.value)}>
                <option value="">Not set — no practice CTA</option>
                <option value="drill">Drill — offer a practice set</option>
                <option value="habit">Habit — guidance only</option>
              </select>
            </div>
            <div className="lm-form-field">
              <label>
                Needs
                <span className="lm-hint">
                  What must change. Knowledge means the student has to understand or remember
                  something first, so the card sends them to revise rather than to more
                  questions — however good the question class behind it is.
                </span>
              </label>
              <select value={form.needs} onChange={e => updateField('needs', e.target.value)}>
                <option value="">Not set — reads as technique</option>
                <option value="knowledge">Knowledge — understand it first</option>
                <option value="technique">Technique — how the attempt is conducted</option>
              </select>
            </div>
          </div>

          {form.needs === 'knowledge' && (
            <div className="lm-form-field">
              <label>
                Concepts to revise
                <span className="lm-hint">
                  Where "learn it" points. A knowledge moment with no concept names a gap and
                  offers nothing.
                </span>
              </label>
              <ConceptPicker
                value={form.conceptIds}
                subjects={form.scope === 'subject' ? form.subjectIds.split(',').map(x => x.trim()).filter(Boolean) : []}
                onChange={ids => setForm(prev => ({ ...prev, conceptIds: ids }))}
              />
            </div>
          )}

          {form.practiceKind === 'drill' && form.needs !== 'knowledge' && (
            <div className="lm-form-field">
              <label>
                Exemplar questions
                <span className="lm-hint">
                  The questions that motivated this moment. Served ahead of the ones detection
                  finds, and the only practice a new moment has until students meet it.
                </span>
              </label>
              <ExemplarQuestionPicker
                value={form.exemplarQuestionIds}
                onChange={ids => setForm(prev => ({ ...prev, exemplarQuestionIds: ids }))}
              />
            </div>
          )}

          <div className="lm-form-row">
            <div className="lm-form-field">
              <label>Tags <span className="lm-hint">Comma-separated</span></label>
              <input type="text" value={form.tags} onChange={e => updateField('tags', e.target.value)} placeholder="e.g. arithmetic, simplification" />
            </div>
            <div className="lm-form-field">
              <label>Difficulty <span className="lm-hint">Comma-separated</span></label>
              <input type="text" value={form.difficulty} onChange={e => updateField('difficulty', e.target.value)} placeholder="e.g. easy, medium, hard" />
            </div>
          </div>

          {saveMutation.error && (
            <div className="lm-error">{saveMutation.error instanceof Error ? saveMutation.error.message : 'Save failed'}</div>
          )}

          <div className="lm-form-actions">
            {/* Deactivating lives here, not in the list: it is a decision about
                one moment made while looking at it, and a row of destructive
                buttons beside every name invites the misclick it deserves. */}
            {editing?.isActive && (
              <button
                className="btn-danger lm-form-deactivate"
                onClick={() => {
                  if (confirm(`Deactivate "${editing.name}"?`)) {
                    deleteMutation.mutate(editing.id, { onSuccess: handleCancel });
                  }
                }}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deactivating…' : 'Deactivate'}
              </button>
            )}
            <button className="btn-secondary" onClick={handleCancel}>Cancel</button>
            <button
              className="btn-primary"
              onClick={() => saveMutation.mutate()}
              disabled={!form.name || !form.description || (!editing?.groupId && !form.detectionHint) || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : (editing ? 'Save' : 'Create')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────────────
  return (
    <div className="lm-page">
      <div className="lm-header">
        <h1>Learning Moments</h1>
        <button className="btn-primary" onClick={handleCreate}>+ Create</button>
      </div>
      <LearningMomentTabs active="definitions" />

      <SearchFilterBar filters={filterDefs} values={filters} onChange={setFilters} hideSearch />

      <DataTable
        columns={columns}
        data={rows}
        rowKey={m => m.id}
        sort={sort}
        onSortChange={setSort}
        onRowClick={m => navigate(`/learning-moments/encounters?type=${m.id}`)}
        rowHref={m => `/learning-moments/encounters?type=${m.id}`}
        loading={isLoading}
        toolbar={moments && (
          <span className="lm-count">
            {rows.length} item{rows.length === 1 ? '' : 's'}
            {rows.length !== moments.length && ` of ${moments.length}`}
          </span>
        )}
        mobileCards
        columnToggleKey="learning-moments"
        emptyMessage="No learning moments found. Create one to get started."
      />

    </div>
  );
}
